import React, { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { UserPlus, X, User, Building, Briefcase, Users, Loader2, Search } from "lucide-react";
import DateDropdownPicker from "../ui/DateDropdownPicker";
import { 
  maritalStatuses, 
  educationLevels, 
  livingConditions,
  companyTypes,
  employeeCounts,
  contractTypes,
  incomeTypes,
  debtCreditTypes,
  currencies,
  romanianBanks
} from "../../utils/mockData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CodebitorSection = ({ codebitorIndex, data, onChange, onRemove }) => {
  const [postalLoading, setPostalLoading] = useState({});
  const [cuiLoading, setCuiLoading] = useState(false);
  const [cuiError, setCuiError] = useState("");

  const handleInputChange = (section, field, value) => {
    const newData = {
      ...data,
      [section]: { ...data[section], [field]: value }
    };
    onChange(newData);
  };

  const handleMultiFieldChange = (section, updates) => {
    const newData = {
      ...data,
      [section]: { ...data[section], ...updates }
    };
    onChange(newData);
  };

  const handleIncomeChange = (index, field, value) => {
    const newIncomes = [...data.income.incomes];
    newIncomes[index] = { ...newIncomes[index], [field]: value };
    handleInputChange('income', 'incomes', newIncomes);
  };

  const addIncome = () => {
    const newIncomes = [...data.income.incomes, { type: "Salariu", value: "" }];
    handleInputChange('income', 'incomes', newIncomes);
  };

  const removeIncome = (index) => {
    const newIncomes = data.income.incomes.filter((_, i) => i !== index);
    handleInputChange('income', 'incomes', newIncomes);
  };

  const handleDebtChange = (index, field, value) => {
    const newDebts = [...data.debts];
    newDebts[index] = { ...newDebts[index], [field]: value };
    onChange({ ...data, debts: newDebts });
  };

  const handleEmployerCUILookup = async () => {
    const cui = (data.employerInfo.cui || "").trim();
    if (!cui || cui.length < 2) {
      setCuiError("Introduceți un CUI valid");
      return;
    }
    setCuiLoading(true);
    setCuiError("");
    try {
      const cleanCUI = cui.replace(/\D/g, "");
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-cui/${cleanCUI}`);
      if (!response.ok) {
        setCuiError("CUI negăsit");
        setCuiLoading(false);
        return;
      }
      const result = await response.json();
      const d = result.data || result;
      const updates = {};
      if (d.denumire || d.company_name) updates.companyName = d.denumire || d.company_name;
      if (d.adresa || d.address) updates.companyAddress = d.adresa || d.address;
      handleMultiFieldChange('employerInfo', updates);
    } catch {
      setCuiError("Eroare la verificare CUI");
    } finally {
      setCuiLoading(false);
    }
  };

  const addDebt = () => {
    const newDebt = {
      id: data.debts.length + 1,
      bankName: "",
      creditType: "Ipotecar",
      grantDate: "",
      remainingBalance: "",
      monthlyRate: "",
      currency: "LEI"
    };
    onChange({ ...data, debts: [...data.debts, newDebt] });
  };

  const removeDebt = (index) => {
    const newDebts = data.debts.filter((_, i) => i !== index);
    onChange({ ...data, debts: newDebts });
  };

  // Postal code lookup
  const handlePostalCodeChange = useCallback(async (prefix, value) => {
    handleInputChange('personalInfo', `${prefix}PostalCode`, value);
    const clean = (value || "").replace(/\D/g, "");
    if (clean.length === 6) {
      setPostalLoading(prev => ({ ...prev, [prefix]: true }));
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/postal-lookup/${clean}`);
        if (res.ok) {
          const result = await res.json();
          handleMultiFieldChange('personalInfo', {
            [`${prefix}PostalCode`]: clean,
            [`${prefix}County`]: result.county || "",
            [`${prefix}City`]: result.city || ""
          });
        }
      } catch { /* ignore */ }
      setPostalLoading(prev => ({ ...prev, [prefix]: false }));
    }
  }, [data]);

  const handleFindPostalCode = useCallback(async (prefix) => {
    const pi = data.personalInfo;
    const street = pi[`${prefix}Street`] || "";
    const number = pi[`${prefix}Number`] || "";
    const city = pi[`${prefix}City`] || pi[`${prefix}County`] || "";
    if (!street || !city) return;

    setPostalLoading(prev => ({ ...prev, [`${prefix}Find`]: true }));
    try {
      const params = new URLSearchParams({ street, number, city });
      if (pi[`${prefix}County`]) params.append("county", pi[`${prefix}County`]);
      const res = await fetch(`${BACKEND_URL}/api/public/address-to-postal?${params}`);
      if (res.ok) {
        const result = await res.json();
        if (result.postalCode) {
          const updates = { [`${prefix}PostalCode`]: result.postalCode };
          if (result.county && !pi[`${prefix}County`]) updates[`${prefix}County`] = result.county;
          if (result.city && !pi[`${prefix}City`]) updates[`${prefix}City`] = result.city;
          handleMultiFieldChange('personalInfo', updates);
        }
      }
    } catch { /* ignore */ }
    setPostalLoading(prev => ({ ...prev, [`${prefix}Find`]: false }));
  }, [data]);

  const pi = data.personalInfo;

  return (
    <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50/30">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-orange-900 flex items-center gap-2">
          <UserPlus className="h-6 w-6" />
          Codebitor #{codebitorIndex}
        </h2>
        <Button type="button" onClick={onRemove} variant="destructive" size="sm" className="flex items-center gap-1">
          <X className="h-4 w-4" /> Elimină Codebitor
        </Button>
      </div>

      {/* Grad de rudenie */}
      <Card className="mb-6">
        <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50 border-b">
          <CardTitle className="text-orange-900">Relația cu Solicitantul Principal</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-2">
            <Label>Grad de Rudenie / Relație *</Label>
            <Select value={data.relationship || ""} onValueChange={(value) => onChange({ ...data, relationship: value })}>
              <SelectTrigger><SelectValue placeholder="Selectați relația" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Soț/Soție">Soț/Soție</SelectItem>
                <SelectItem value="Frate/Soră">Frate/Soră</SelectItem>
                <SelectItem value="Părinte/Copil">Părinte/Copil</SelectItem>
                <SelectItem value="Prieten">Prieten</SelectItem>
                <SelectItem value="Asociat/Colaborator">Asociat/Colaborator</SelectItem>
                <SelectItem value="Altă rudă">Altă rudă</SelectItem>
                <SelectItem value="Altul">Altul</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* ══════════════════ DATE PERSONALE (identic cu PersonalInfoSection) ══════════════════ */}
      <Card className="mb-6">
        <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50 border-b">
          <CardTitle className="flex items-center gap-2 text-orange-900">
            <User className="h-5 w-5" /> Date Personale Codebitor
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Nume *</Label>
              <Input value={pi.lastName || ""} onChange={(e) => handleInputChange('personalInfo', 'lastName', e.target.value)} placeholder="Introduceți numele" />
            </div>
            <div className="space-y-2">
              <Label>Prenume *</Label>
              <Input value={pi.firstName || ""} onChange={(e) => handleInputChange('personalInfo', 'firstName', e.target.value)} placeholder="Introduceți prenumele" />
            </div>

            {/* Sex */}
            <div className="space-y-2">
              <Label>Sex *</Label>
              <Select value={pi.sex || ""} onValueChange={(value) => handleInputChange('personalInfo', 'sex', value)}>
                <SelectTrigger><SelectValue placeholder="Selectați" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Masculin">Masculin</SelectItem>
                  <SelectItem value="Feminin">Feminin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Data nașterii - DateDropdownPicker */}
            <div className="space-y-2">
              <Label>Data Nașterii *</Label>
              <DateDropdownPicker
                id={`codeb-birthDate-${codebitorIndex}`}
                value={pi.birthDate || ""}
                onChange={(val) => handleInputChange('personalInfo', 'birthDate', val)}
              />
            </div>

            {/* CNP */}
            <div className="space-y-2">
              <Label>CNP *</Label>
              <Input value={pi.cnp || ""} onChange={(e) => handleInputChange('personalInfo', 'cnp', e.target.value)} placeholder="Codul numeric personal" maxLength={13} />
            </div>

            {/* Cetățenie */}
            <div className="space-y-2">
              <Label>Cetățenie</Label>
              <Input value={pi.citizenship || "Română"} onChange={(e) => handleInputChange('personalInfo', 'citizenship', e.target.value)} placeholder="Ex: Română" />
            </div>

            {/* Act identitate */}
            <div className="space-y-2">
              <Label>Tip Act Identitate</Label>
              <Select value={pi.idType || "CI"} onValueChange={(value) => handleInputChange('personalInfo', 'idType', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CI">Carte de Identitate</SelectItem>
                  <SelectItem value="Pașaport">Pașaport</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Serie Act *</Label>
              <Input value={pi.idSeries || ""} onChange={(e) => handleInputChange('personalInfo', 'idSeries', e.target.value)} placeholder="Ex: AB" />
            </div>
            <div className="space-y-2">
              <Label>Număr Act *</Label>
              <Input value={pi.idNumber || ""} onChange={(e) => handleInputChange('personalInfo', 'idNumber', e.target.value)} placeholder="Numărul actului" />
            </div>
            <div className="space-y-2">
              <Label>Emitent *</Label>
              <Input value={pi.idIssuer || ""} onChange={(e) => handleInputChange('personalInfo', 'idIssuer', e.target.value)} placeholder="Ex: SPCLEP București" />
            </div>

            {/* Date eliberare/expirare - DateDropdownPicker */}
            <div className="space-y-2">
              <Label>Data Eliberare *</Label>
              <DateDropdownPicker
                id={`codeb-idIssueDate-${codebitorIndex}`}
                value={pi.idIssueDate || ""}
                onChange={(val) => handleInputChange('personalInfo', 'idIssueDate', val)}
              />
            </div>
            <div className="space-y-2">
              <Label>Data Expirare *</Label>
              <DateDropdownPicker
                id={`codeb-idExpiryDate-${codebitorIndex}`}
                value={pi.idExpiryDate || ""}
                onChange={(val) => handleInputChange('personalInfo', 'idExpiryDate', val)}
                maxYear={2040}
              />
            </div>

            {/* Stare civilă, studii, copii */}
            <div className="space-y-2">
              <Label>Stare Civilă</Label>
              <Select value={pi.maritalStatus || "Necăsătorit"} onValueChange={(value) => handleInputChange('personalInfo', 'maritalStatus', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{maritalStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Studii Finalizate</Label>
              <Select value={pi.education || "Superioare"} onValueChange={(value) => handleInputChange('personalInfo', 'education', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{educationLevels.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Număr Copii în Întreținere</Label>
              <Input type="number" value={pi.childrenCount || "0"} onChange={(e) => handleInputChange('personalInfo', 'childrenCount', e.target.value)} min="0" />
            </div>
            <div className="space-y-2">
              <Label>Numele de Fată al Mamei</Label>
              <Input value={pi.motherMaidenName || ""} onChange={(e) => handleInputChange('personalInfo', 'motherMaidenName', e.target.value)} placeholder="Pentru identificare bancară" />
            </div>
            <div className="space-y-2">
              <Label>Condiție Locativă</Label>
              <Select value={pi.livingCondition || "Cu părinții"} onValueChange={(value) => handleInputChange('personalInfo', 'livingCondition', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{livingConditions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Contact */}
            <div className="space-y-2">
              <Label>Telefon *</Label>
              <Input value={pi.phone || ""} onChange={(e) => handleInputChange('personalInfo', 'phone', e.target.value)} placeholder="Numărul de telefon" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={pi.email || ""} onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)} placeholder="Adresa de email" />
            </div>
          </div>

          {/* ── ADRESĂ DOMICILIU (structurată, identic cu debitorul) ── */}
          <div className="mt-6 p-4 border border-orange-200 rounded-lg bg-orange-50/30">
            <h3 className="text-sm font-semibold text-orange-900 mb-4">Adresă Domiciliu</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Județ / Sector *</Label>
                <Input value={pi.domCounty || ""} onChange={(e) => handleInputChange('personalInfo', 'domCounty', e.target.value)} placeholder="Ex: Sector 2 / Cluj" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Localitate</Label>
                <Input value={pi.domCity || ""} onChange={(e) => handleInputChange('personalInfo', 'domCity', e.target.value)} placeholder="Ex: București / Cluj-Napoca" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Strada *</Label>
                <Input value={pi.domStreet || ""} onChange={(e) => handleInputChange('personalInfo', 'domStreet', e.target.value)} placeholder="Numele străzii" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Nr. *</Label>
                <Input value={pi.domNumber || ""} onChange={(e) => handleInputChange('personalInfo', 'domNumber', e.target.value)} placeholder="Nr." />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Bloc</Label>
                <Input value={pi.domBlock || ""} onChange={(e) => handleInputChange('personalInfo', 'domBlock', e.target.value)} placeholder="Bloc" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Scara</Label>
                <Input value={pi.domStaircase || ""} onChange={(e) => handleInputChange('personalInfo', 'domStaircase', e.target.value)} placeholder="Sc." />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Etaj</Label>
                <Input value={pi.domFloor || ""} onChange={(e) => handleInputChange('personalInfo', 'domFloor', e.target.value)} placeholder="Et." />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Apartament</Label>
                <Input value={pi.domApartment || ""} onChange={(e) => handleInputChange('personalInfo', 'domApartment', e.target.value)} placeholder="Ap." />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Cod Poștal</Label>
                <div className="flex gap-1">
                  <div className="relative flex-1">
                    <Input value={pi.domPostalCode || ""} onChange={(e) => handlePostalCodeChange('dom', e.target.value)} placeholder="Ex: 021587" maxLength={6} />
                    {postalLoading.dom && <Loader2 className="h-3 w-3 animate-spin absolute right-2 top-3 text-blue-500" />}
                  </div>
                  <button type="button" onClick={() => handleFindPostalCode('dom')} disabled={postalLoading.domFind || (!pi.domStreet && !pi.domCity)}
                    className="px-2 h-9 border border-orange-300 rounded bg-orange-50 hover:bg-orange-100 disabled:opacity-40 text-orange-600 text-xs font-medium shrink-0">
                    {postalLoading.domFind ? <Loader2 className="h-3 w-3 animate-spin" /> : "Caută"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── ADRESĂ REȘEDINȚĂ (structurată) ── */}
          <div className="mt-4 p-4 border border-yellow-200 rounded-lg bg-yellow-50/30">
            <h3 className="text-sm font-semibold text-yellow-900 mb-1">Adresă Reședință</h3>
            <p className="text-xs text-gray-500 mb-4">Completați doar dacă diferă de domiciliu.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Județ / Sector</Label>
                <Input value={pi.resCounty || ""} onChange={(e) => handleInputChange('personalInfo', 'resCounty', e.target.value)} placeholder="Ex: Sector 1 / Ilfov" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Localitate</Label>
                <Input value={pi.resCity || ""} onChange={(e) => handleInputChange('personalInfo', 'resCity', e.target.value)} placeholder="Ex: București / Voluntari" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Strada</Label>
                <Input value={pi.resStreet || ""} onChange={(e) => handleInputChange('personalInfo', 'resStreet', e.target.value)} placeholder="Numele străzii" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Nr.</Label>
                <Input value={pi.resNumber || ""} onChange={(e) => handleInputChange('personalInfo', 'resNumber', e.target.value)} placeholder="Nr." />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Bloc</Label>
                <Input value={pi.resBlock || ""} onChange={(e) => handleInputChange('personalInfo', 'resBlock', e.target.value)} placeholder="Bloc" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Scara</Label>
                <Input value={pi.resStaircase || ""} onChange={(e) => handleInputChange('personalInfo', 'resStaircase', e.target.value)} placeholder="Sc." />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Etaj</Label>
                <Input value={pi.resFloor || ""} onChange={(e) => handleInputChange('personalInfo', 'resFloor', e.target.value)} placeholder="Et." />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Apartament</Label>
                <Input value={pi.resApartment || ""} onChange={(e) => handleInputChange('personalInfo', 'resApartment', e.target.value)} placeholder="Ap." />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Cod Poștal</Label>
                <div className="flex gap-1">
                  <div className="relative flex-1">
                    <Input value={pi.resPostalCode || ""} onChange={(e) => handlePostalCodeChange('res', e.target.value)} placeholder="Ex: 077190" maxLength={6} />
                    {postalLoading.res && <Loader2 className="h-3 w-3 animate-spin absolute right-2 top-3 text-blue-500" />}
                  </div>
                  <button type="button" onClick={() => handleFindPostalCode('res')} disabled={postalLoading.resFind || (!pi.resStreet && !pi.resCity)}
                    className="px-2 h-9 border border-orange-300 rounded bg-orange-50 hover:bg-orange-100 disabled:opacity-40 text-orange-600 text-xs font-medium shrink-0">
                    {postalLoading.resFind ? <Loader2 className="h-3 w-3 animate-spin" /> : "Caută"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* PEP */}
          <div className="mt-6">
            <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
              <div className="flex items-start space-x-2">
                <Checkbox
                  id={`isPoliticallyExposed-${codebitorIndex}`}
                  checked={pi.isPoliticallyExposed || false}
                  onCheckedChange={(checked) => handleInputChange('personalInfo', 'isPoliticallyExposed', checked)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor={`isPoliticallyExposed-${codebitorIndex}`} className="text-base font-semibold text-yellow-900 cursor-pointer">
                    Persoană Expusă Politic (PEP)
                  </Label>
                  <p className="text-xs text-yellow-800 mt-1">
                    Bifați dacă sunteți sau ați fost în ultimii 12 luni într-o funcție publică importantă.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ══════════════════ DATE ANGAJATOR ══════════════════ */}
      <Card className="mb-6">
        <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50 border-b">
          <CardTitle className="flex items-center gap-2 text-orange-900">
            <Building className="h-5 w-5" /> Date Angajator
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Nume Companie *</Label>
              <Input value={data.employerInfo.companyName || ""} onChange={(e) => handleInputChange('employerInfo', 'companyName', e.target.value)} placeholder="Numele companiei" />
            </div>
            <div className="space-y-2">
              <Label>CUI Companie</Label>
              <div className="flex gap-2">
                <Input
                  value={data.employerInfo.cui || ""}
                  onChange={(e) => handleInputChange('employerInfo', 'cui', e.target.value)}
                  placeholder="Codul unic de identificare"
                  className="flex-1"
                  data-testid={`codebitor-cui-${codebitorIndex}`}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleEmployerCUILookup}
                  disabled={cuiLoading}
                  className="px-3"
                  data-testid={`codebitor-cui-lookup-btn-${codebitorIndex}`}
                >
                  {cuiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
              {cuiError && <p className="text-xs text-red-500">{cuiError}</p>}
              <p className="text-xs text-gray-400">Apăsați lupa pentru a completa automat datele firmei</p>
            </div>
            <div className="col-span-1 md:col-span-2 space-y-2">
              <Label>Adresă Companie</Label>
              <Input value={data.employerInfo.companyAddress || ""} onChange={(e) => handleInputChange('employerInfo', 'companyAddress', e.target.value)} placeholder="Adresa completă a companiei" />
            </div>
            <div className="space-y-2">
              <Label>Tip Companie</Label>
              <Select value={data.employerInfo.companyType || "Privat român"} onValueChange={(value) => handleInputChange('employerInfo', 'companyType', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{companyTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Număr Angajați</Label>
              <Select value={data.employerInfo.employeeCount || "<20"} onValueChange={(value) => handleInputChange('employerInfo', 'employeeCount', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{employeeCounts.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tip Contract *</Label>
              <Select value={data.employerInfo.contractType || "Nedeterminată"} onValueChange={(value) => handleInputChange('employerInfo', 'contractType', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{contractTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Perioada Contract</Label>
              <Input value={data.employerInfo.contractPeriod || ""} onChange={(e) => handleInputChange('employerInfo', 'contractPeriod', e.target.value)} placeholder="Ex: 2 ani, nedeterminată" />
            </div>
            <div className="space-y-2">
              <Label>Data Angajării *</Label>
              <DateDropdownPicker
                id={`codeb-hireDate-${codebitorIndex}`}
                value={data.employerInfo.hireDate || ""}
                onChange={(val) => handleInputChange('employerInfo', 'hireDate', val)}
              />
            </div>
            <div className="space-y-2">
              <Label>Vechime Totală (ani)</Label>
              <Input type="number" value={data.employerInfo.totalSeniority || ""} onChange={(e) => handleInputChange('employerInfo', 'totalSeniority', e.target.value)} placeholder="Vechimea în muncă" step="0.5" />
            </div>
            <div className="space-y-2">
              <Label>Profesia *</Label>
              <Input value={data.employerInfo.profession || ""} onChange={(e) => handleInputChange('employerInfo', 'profession', e.target.value)} placeholder="Profesia exercitată" />
            </div>
            <div className="space-y-2">
              <Label>Funcția Actuală *</Label>
              <Input value={data.employerInfo.currentPosition || ""} onChange={(e) => handleInputChange('employerInfo', 'currentPosition', e.target.value)} placeholder="Funcția ocupată" />
            </div>
            <div className="space-y-2">
              <Label>Cod CAEN</Label>
              <Input value={data.employerInfo.caenCode || ""} onChange={(e) => handleInputChange('employerInfo', 'caenCode', e.target.value)} placeholder="Codul de activitate economică" />
            </div>
            <div className="col-span-1 md:col-span-2 space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox id={`hasInterruptions-${codebitorIndex}`} checked={data.employerInfo.hasInterruptions || false}
                  onCheckedChange={(checked) => handleInputChange('employerInfo', 'hasInterruptions', checked)} />
                <Label htmlFor={`hasInterruptions-${codebitorIndex}`}>Au existat concedii/întreruperi de muncă în ultimii 2 ani?</Label>
              </div>
              {data.employerInfo.hasInterruptions && (
                <div className="space-y-2">
                  <Label>Detalii Întreruperi</Label>
                  <Input value={data.employerInfo.interruptions || ""} onChange={(e) => handleInputChange('employerInfo', 'interruptions', e.target.value)} placeholder="Tipul și durata întreruperilor" />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ══════════════════ VENITURI ══════════════════ */}
      <Card className="mb-6">
        <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50 border-b">
          <CardTitle className="flex items-center gap-2 text-orange-900">
            <Briefcase className="h-5 w-5" /> Venituri Codebitor
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Venituri Principale</h3>
              <Button type="button" onClick={addIncome} variant="outline" size="sm">+ Adaugă Venit</Button>
            </div>
            {data.income.incomes.map((income, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Tip Venit {index + 1}</Label>
                    <Select value={income.type} onValueChange={(value) => handleIncomeChange(index, 'type', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{incomeTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Valoare (LEI)</Label>
                    <Input type="number" value={income.value} onChange={(e) => handleIncomeChange(index, 'value', e.target.value)} placeholder="Valoarea în LEI" />
                  </div>
                  <div className="flex items-end">
                    {data.income.incomes.length > 1 && (
                      <Button type="button" onClick={() => removeIncome(index)} variant="destructive" size="sm"><X className="h-4 w-4" /></Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ══════════════════ DATORII ══════════════════ */}
      <Card className="mb-6">
        <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50 border-b">
          <CardTitle className="flex items-center gap-2 text-orange-900">
            <Users className="h-5 w-5" /> Datorii Existente Codebitor
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">Creditele și împrumuturile codebitorului</p>
              <Button type="button" onClick={addDebt} variant="outline" size="sm">+ Adaugă Credit/Datorie</Button>
            </div>
            {data.debts.map((debt, index) => (
              <div key={debt.id} className="p-6 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Credit/Datorie #{index + 1}</h3>
                  {data.debts.length > 1 && (
                    <Button type="button" onClick={() => removeDebt(index)} variant="destructive" size="sm"><X className="h-4 w-4" /></Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Nume Bancă</Label>
                    <Select value={debt.bankName || ""} onValueChange={(value) => handleDebtChange(index, 'bankName', value)}>
                      <SelectTrigger><SelectValue placeholder="Selectați banca" /></SelectTrigger>
                      <SelectContent>{romanianBanks.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tip Credit</Label>
                    <Select value={debt.creditType || "Ipotecar"} onValueChange={(value) => handleDebtChange(index, 'creditType', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{debtCreditTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Data Acordare</Label>
                    <DateDropdownPicker
                      id={`codeb-debt-grantDate-${codebitorIndex}-${index}`}
                      value={debt.grantDate || ""}
                      onChange={(val) => handleDebtChange(index, 'grantDate', val)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sold Rămas</Label>
                    <div className="flex gap-2">
                      <Input type="number" value={debt.remainingBalance || ""} onChange={(e) => handleDebtChange(index, 'remainingBalance', e.target.value)} placeholder="Soldul rămas" className="flex-1" />
                      <Select value={debt.currency || "LEI"} onValueChange={(value) => handleDebtChange(index, 'currency', value)}>
                        <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                        <SelectContent>{currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Rata Lunară</Label>
                    <div className="flex gap-2">
                      <Input type="number" value={debt.monthlyRate || ""} onChange={(e) => handleDebtChange(index, 'monthlyRate', e.target.value)} placeholder="Rata lunară" className="flex-1" />
                      <Select value={debt.rateCurrency || debt.currency || "LEI"} onValueChange={(value) => handleDebtChange(index, 'rateCurrency', value)}>
                        <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                        <SelectContent>{currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 md:col-span-2">
                    <Checkbox
                      id={`codeb-debt-refinance-${codebitorIndex}-${index}`}
                      checked={debt.willRefinance || false}
                      onCheckedChange={(checked) => handleDebtChange(index, 'willRefinance', checked)}
                    />
                    <Label htmlFor={`codeb-debt-refinance-${codebitorIndex}-${index}`}>Se refinanțează prin acest credit?</Label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CodebitorSection;
